import { Controller } from '@hotwired/stimulus';
import { Turbo } from '@hotwired/turbo-rails';
import { transitionSteps, transitionStepsAsync } from '../lib/step_transition';
import { getCsrfToken } from '../lib/csrf';
import { showToast } from '../lib/toastr';

export default class extends Controller {
  static targets = [
    'amountInput',
    'step1',
    'step2',
    'cardOption',
    'cardAmountField',
    'loadingStep',
    'wireStep',
    'cardStep',
    'amountDisplay',
    'loadingMessage',
  ];

  static values = {
    maxCardAmount: { type: Number, default: 1500 },
    hasViban: { type: Boolean, default: false },
    generateVibanUrl: String,
    vibanLoadingMessage: String,
    cardLoadingMessage: String,
    networkErrorMessage: String,
  };

  disconnect() {
    if (this.pendingTimeout) {
      clearTimeout(this.pendingTimeout);
      this.pendingTimeout = null;
    }
  }

  selectPreset(event) {
    const amount = event.currentTarget.dataset.amount;
    this.amountInputTarget.value = amount;
    this.clearAmountError();
  }

  clearAmountError() {
    this.amountInputTarget.classList.remove('is-invalid');
    this.amountInputTarget.removeAttribute('aria-invalid');
  }

  goToStep2() {
    const amount = parseInt(this.amountInputTarget.value, 10);

    // 0 and an empty field are both falsy.
    if (!amount || amount < 1) {
      this.amountInputTarget.classList.add('is-invalid');
      this.amountInputTarget.setAttribute('aria-invalid', 'true');
      this.amountInputTarget.focus();
      return;
    }

    this.clearAmountError();

    this.amountDisplayTargets.forEach(el => {
      el.textContent = `${amount} €`;
    });
    this.cardAmountFieldTarget.value = amount;

    if (amount > this.maxCardAmountValue) {
      this.cardOptionTarget.classList.add('d-none');
    } else {
      this.cardOptionTarget.classList.remove('d-none');
    }

    this.#transition(this.step1Target, this.step2Target, 'forward');
  }

  goBack() {
    this.#transition(this.step2Target, this.step1Target, 'backward');
  }

  async selectCard() {
    const form = this.cardAmountFieldTarget.form;

    this.#setLoadingMessage(this.cardLoadingMessageValue);

    await transitionStepsAsync(
      this.step2Target,
      this.loadingStepTarget,
      'forward'
    );

    try {
      const response = await fetch(form.action, {
        method: 'POST',
        headers: {
          Accept: 'text/vnd.turbo-stream.html',
          'X-CSRF-Token': getCsrfToken(),
        },
        body: new FormData(form),
        credentials: 'same-origin',
      });

      // Left the funnel: follow it so the flash is shown.
      if (response.redirected) {
        window.Turbo.visit(response.url);
        return;
      }

      // 422 carries the error stream that renders inside the card step.
      if (!response.ok && response.status !== 422) {
        this.#transition(this.loadingStepTarget, this.step2Target, 'backward');
        return;
      }

      Turbo.renderStreamMessage(await response.text());
    } catch {
      showToast('error', this.networkErrorMessageValue);
      this.#transition(this.loadingStepTarget, this.step2Target, 'backward');
      return;
    }

    this.#transition(this.loadingStepTarget, this.cardStepTarget, 'forward');
  }

  backToAmountFromCard() {
    this.#transition(this.cardStepTarget, this.step1Target, 'backward');
  }

  backToStep2FromCard() {
    this.#transition(this.cardStepTarget, this.step2Target, 'backward');
  }

  async selectWire() {
    if (this.hasVibanValue) {
      this.#transition(this.step2Target, this.wireStepTarget, 'forward');
      return;
    }

    this.#setLoadingMessage(this.vibanLoadingMessageValue);

    await transitionStepsAsync(this.step2Target, this.loadingStepTarget, 'forward');

    try {
      const amount = this.amountInputTarget.value;
      const url = new URL(this.generateVibanUrlValue, window.location.origin);
      if (amount) url.searchParams.set('amount', amount);

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          Accept: 'text/vnd.turbo-stream.html',
          'X-CSRF-Token': getCsrfToken(),
        },
        credentials: 'same-origin',
      });

      if (response.ok) {
        const html = await response.text();
        Turbo.renderStreamMessage(html);
        this.hasVibanValue = true;
      } else {
        this.#transition(this.loadingStepTarget, this.step2Target, 'backward');
        return;
      }
    } catch {
      this.#transition(this.loadingStepTarget, this.step2Target, 'backward');
      return;
    }

    this.#transition(this.loadingStepTarget, this.wireStepTarget, 'forward');
  }

  backToStep2() {
    this.#transition(this.wireStepTarget, this.step2Target, 'backward');
  }

  // The loading step is shared by the wire and card paths, so its wording is
  // set by whichever path is about to use it.
  #setLoadingMessage(message) {
    this.loadingMessageTargets.forEach(el => {
      el.textContent = message;
    });
  }

  #transition(outEl, inEl, direction) {
    if (this.pendingTimeout) {
      clearTimeout(this.pendingTimeout);
    }
    this.pendingTimeout = transitionSteps(outEl, inEl, direction);
  }

}
